/**
 * Compute the time ranges in a visible week that are *outside* the
 * common-availability windows, so the calendar can grey them out.
 *
 *   visible week = Sun 00:00 → Sat 23:59
 *   availability = [ Mon 09:00 – 17:00, Tue 09:00 – 17:00, ... ]
 *   result       = [ Sun 00:00 – Sat 23:59 minus each availability ]
 *
 * The caller passes a visible range (from FullCalendar's `datesSet`)
 * and the already-computed common availability. We walk each day in
 * the range, clip availability to it, and emit the gaps.
 *
 * Past days are skipped — the existing past-time background event
 * already covers them.
 */
import moment from 'moment-timezone';
import type { Interval } from './types';

export interface BuildUnavailableHoursArgs {
  /** Visible week start (any ISO in the range). */
  visibleStartIso: string;
  /** Visible week end. */
  visibleEndIso: string;
  /** Common availability intervals for the week. */
  availability: Interval[];
  timezone: string;
}

export function buildUnavailableHoursIntervals({
  visibleStartIso,
  visibleEndIso,
  availability,
  timezone,
}: BuildUnavailableHoursArgs): Interval[] {
  const rangeStart = moment.tz(visibleStartIso, timezone).startOf('day');
  const rangeEnd = moment.tz(visibleEndIso, timezone).endOf('day');
  if (!rangeStart.isValid() || !rangeEnd.isValid()) {
    return [];
  }

  const now = moment().tz(timezone);
  const parsedAvailability = availability
    .map(({ start, end }) => ({
      start: moment.tz(start, timezone),
      end: moment.tz(end, timezone),
    }))
    .filter(
      ({ start, end }) =>
        start.isValid() && end.isValid() && start.isBefore(end)
    );

  const out: Interval[] = [];

  let day = rangeStart.clone();
  while (day.isSameOrBefore(rangeEnd, 'day')) {
    if (day.isBefore(now, 'day')) {
      day = day.clone().add(1, 'day');
      continue;
    }

    const dayStart = day.clone().startOf('day');
    const dayEnd = day.clone().endOf('day');

    const slotsForDay = parsedAvailability
      .filter((iv) => iv.start.isBefore(dayEnd) && iv.end.isAfter(dayStart))
      .map((iv) => ({
        start: moment.max(iv.start, dayStart),
        end: moment.min(iv.end, dayEnd),
      }))
      .filter((iv) => iv.end.isAfter(iv.start))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    let cursor = dayStart.clone();
    for (const slot of slotsForDay) {
      if (slot.start.isAfter(cursor)) {
        out.push({
          start: cursor.toISOString(),
          end: slot.start.toISOString(),
        });
      }
      if (slot.end.isAfter(cursor)) {
        cursor = slot.end.clone();
      }
    }
    if (cursor.isBefore(dayEnd)) {
      out.push({
        start: cursor.toISOString(),
        end: dayEnd.toISOString(),
      });
    }

    day = day.clone().add(1, 'day');
  }

  return out;
}
